/**
 * Fullscreen live-AR view.
 *
 * The camera feed and the mirrored robot render into this overlay's canvas
 * while the builder keeps running underneath — that is what keeps the robot
 * live: the websocket still updates the store, the store still poses the
 * builder scene, and the mirror copies it into the room every frame. The
 * joint sheet is rendered on top of the feed, so the robot can be posed while
 * looking at it standing on the floor.
 */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ActionIcon, Box, Text } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { getSceneUtils } from "../scene";
import { MobileJointSheet } from "../panels/MobileJointSheet";
import { useViewerStore } from "../store";
import { AR_OVERLAY_Z_INDEX, startLiveAr, type LiveArSession } from "./arLiveEngine";
import { jointForPart } from "./arJointPick";
import { LIVE_AR_UI_ATTRIBUTE } from "./arLiveWebXr";

interface ArOverlayProps {
  onClose: () => void;
  onError: (message: string) => void;
}

/**
 * On the WebXR path a touch on these elements would otherwise also be an XR
 * `select` — a joint drag that teleports the robot. The attribute is what
 * `beforexrselect` suppression matches on; the 8th Wall path ignores it.
 */
const uiElement = { [LIVE_AR_UI_ATTRIBUTE]: "true" };

/** Finds the overlay's dom-overlay root in tests and devtools. */
export const AR_OVERLAY_ROOT_ATTRIBUTE = "data-live-ar-overlay-root";

export function ArOverlay({ onClose, onError }: ArOverlayProps) {
  // The dom-overlay root must not be an element React manages: Variant
  // Launch emulates dom-overlay by hiding everything on the page outside the
  // root, and its docs want the root itself framework-free, with framework
  // content portalled in as children. So the root is a bare div on the body,
  // and all of the overlay renders into it through a portal.
  const [overlayHost] = useState(() => {
    const host = document.createElement("div");
    host.setAttribute(AR_OVERLAY_ROOT_ATTRIBUTE, "true");
    return host;
  });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [starting, setStarting] = useState(true);
  // `?ar-debug=1`: report what each tap found, on the phone where the only
  // real answers are.
  const [debugging] = useState(
    () => new URLSearchParams(window.location.search).get("ar-debug") === "1"
  );
  const [tapReport, setTapReport] = useState<string | null>(null);

  useEffect(() => {
    document.body.appendChild(overlayHost);
    return () => {
      overlayHost.remove();
    };
  }, [overlayHost]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    let session: LiveArSession | null = null;
    let cancelled = false;

    startLiveAr(canvas, () => getSceneUtils()?.getScene() ?? null, {
      // The camera feed arriving is what turns this from a black canvas into
      // AR, and it happens after the engine's permission prompt, not when the
      // pipeline attaches.
      onCameraReady: () => {
        if (!cancelled) {
          setStarting(false);
        }
      },
      onCameraError: (error) => {
        if (!cancelled) {
          onError(error.message);
          onClose();
        }
      },
      // Pointing at the part to move beats hunting for its name in a row
      // of chips where every module offers a "joint1". Read from the store
      // at tap time: the robot can gain and lose modules while AR is open.
      onRobotPartTapped: (part) => {
        const { joints, instances, setSelectedJointName } = useViewerStore.getState();
        const joint = jointForPart(part, joints, instances);
        if (joint) {
          setSelectedJointName(joint.name);
        }
      },
      onTapDiagnostics: (diagnostics) => {
        if (!cancelled && debugging) {
          const { joints, instances } = useViewerStore.getState();
          const joint = diagnostics.part ? jointForPart(diagnostics.part, joints, instances) : null;
          setTapReport(
            [
              `pointer=${diagnostics.hadPointer}`,
              `xrRay=${diagnostics.hadXrRay}`,
              `parts=${diagnostics.mirroredParts}`,
              `link=${diagnostics.part?.linkName ?? "-"}`,
              `inst=${diagnostics.part?.instanceId ?? "-"}`,
              `joint=${joint?.name ?? "-"}`,
              `joints=${joints.length}`,
            ].join(" ")
          );
        }
      },
      // The host is the WebXR dom-overlay root: it is what keeps the joint
      // sheet and exit button visible over an ARCore session.
    }, overlayHost)
      .then((started) => {
        if (cancelled) {
          started.stop();
          return;
        }
        session = started;
      })
      .catch((e) => {
        // A rejection landing after the user already closed the overlay is
        // not news; showing it would put an error over a view they dismissed.
        if (cancelled) {
          return;
        }
        onError(e instanceof Error ? e.message : String(e));
        onClose();
      });

    return () => {
      cancelled = true;
      session?.stop();
    };
  }, [onClose, onError, overlayHost, debugging]);

  return createPortal(
    <Box
      style={{
        position: "fixed",
        inset: 0,
        // Below the engine's own UI, so its permission prompt is on top.
        zIndex: AR_OVERLAY_Z_INDEX,
        // No backdrop: the engine moves the canvas out to the body, and an
        // opaque layer here covers the camera feed rather than framing it.
        display: "flex",
        flexDirection: "column",
        // Only the controls take touches; the rest belongs to the canvas
        // underneath, which is what the engine reads gestures from.
        pointerEvents: "none",
      }}
    >
      {/* Out of the flex flow on purpose: the 8th Wall engine reparents this
          to the body, but the WebXR path leaves it here, and a canvas taking
          part in the column pushes the joint sheet off the bottom. */}
      <canvas
        ref={canvasRef}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", touchAction: "none" }}
      />

      {starting && (
        <Text
          size="sm"
          c="white"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          Starting the camera...
        </Text>
      )}

      {!starting && (
        <Text
          size="xs"
          c="white"
          ta="center"
          style={{
            position: "absolute",
            top: "calc(env(safe-area-inset-top) + 12px)",
            left: 12,
            // Clear of the exit button, which shares this corner.
            right: 68,
            textShadow: "0 1px 3px rgba(0,0,0,0.8)",
            pointerEvents: "none",
          }}
        >
          Tap the floor or a table to stand the robot on it
        </Text>
      )}

      {debugging && tapReport && (
        <Text
          size="xs"
          c="lime"
          style={{
            position: "absolute",
            top: "calc(env(safe-area-inset-top) + 40px)",
            left: 12,
            right: 12,
            fontFamily: "monospace",
            textShadow: "0 1px 3px rgba(0,0,0,0.9)",
            pointerEvents: "none",
          }}
        >
          {tapReport}
        </Text>
      )}

      <ActionIcon
        variant="filled"
        color="dark"
        size={44}
        radius="xl"
        aria-label="Exit AR"
        onClick={onClose}
        {...uiElement}
        style={{
          position: "absolute",
          top: "calc(env(safe-area-inset-top) + 8px)",
          right: 12,
          pointerEvents: "auto",
        }}
      >
        <IconX size={22} />
      </ActionIcon>

      {/* The joint sheet over the camera feed: posing while looking at the
          robot in the room is the point of live AR. */}
      <Box
        {...uiElement}
        style={{
          flexShrink: 0,
          // The engine takes the canvas out of this flex flow, leaving the
          // sheet as the only item in it - which floats it to the top of the
          // screen, into the exit button, unless it is pushed down.
          marginTop: "auto",
          backgroundColor: "var(--mantine-color-body)",
          paddingBottom: "env(safe-area-inset-bottom)",
          pointerEvents: "auto",
        }}
      >
        <MobileJointSheet compact />
      </Box>
    </Box>,
    overlayHost
  );
}
